import { useCallback, useRef, useState } from "react";
import {
  fetchAuditNames,
  fetchGovernanceAudit,
  fetchGovernanceAuditPage,
  type AuditNames,
  type GovernanceAuditEntry,
  type GovernanceAuditPage,
} from "@/lib/api/organizations";
import {
  usePagedLog,
  type PagedLog,
} from "@/components/main/governance/usePagedLog";

const distinct = (values: unknown[]): string[] => [
  ...new Set(
    values.filter((v): v is string => typeof v === "string" && v !== ""),
  ),
];

export interface AuditFeed extends PagedLog<GovernanceAuditEntry> {
  names: AuditNames;
}

/// One page of governance events, plus paging. `spaceId` narrows to a single
/// space; omit it for the whole org. A page lands together with the names of
/// what it mentions, so no row flashes "unknown" before they arrive.
export function useGovernanceAudit(
  orgId: string | null,
  spaceId?: string,
  pageSize = 100,
  admin = false,
): AuditFeed {
  const [names, setNames] = useState<AuditNames>({ models: {}, spaces: {} });
  const asked = useRef(new Set<string>());

  /// Asks only for ids no earlier page asked for.
  const namesFor = useCallback(
    (org: string, page: GovernanceAuditEntry[]): Promise<AuditNames> => {
      const unasked = (ids: string[]) =>
        ids.filter((id) => !asked.current.has(id));
      const models = unasked(distinct(page.map((e) => e.detail?.["model_id"])));
      const spaces = unasked(
        distinct(
          page.flatMap((e) => [
            e.space_id,
            e.detail?.["from_space_id"],
            e.detail?.["with"],
          ]),
        ),
      );
      [...models, ...spaces].forEach((id) => asked.current.add(id));
      return fetchAuditNames(org, { models, spaces }, admin);
    },
    [admin],
  );

  const feed = usePagedLog<GovernanceAuditEntry, number, GovernanceAuditPage>({
    key: orgId && `${orgId}|${spaceId ?? ""}|${pageSize}|${admin}`,
    first: () =>
      fetchGovernanceAudit(orgId!, { spaceId, limit: pageSize, admin }),
    next: (before) =>
      fetchGovernanceAuditPage(orgId!, {
        spaceId,
        before,
        limit: pageSize,
        admin,
      }),
    onPage: async (page, first) => {
      if (first) asked.current = new Set();
      const found = await namesFor(orgId!, page.entries);
      return () =>
        setNames((prev) =>
          first
            ? found
            : {
                models: { ...prev.models, ...found.models },
                spaces: { ...prev.spaces, ...found.spaces },
              },
        );
    },
    errorLabel: "Could not load more events. Try again.",
  });

  return { ...feed, names };
}
